import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { pinia } from '@/stores/pinia'
import {
  ROUTE_TITLES,
  OPS_ROUTE_TITLES,
  APP_NAME,
  APP_FULL_NAME,
  DEFAULT_REDIRECT_PATH,
  ERROR_PAGE_TEXT
} from '@/constants/routes'
import { isPlatformOperator } from '@/utils/platform-access'
import { resolveUserPermissions } from '@/utils/permissions'
import { moduleOfPath } from '@/views/next/nav/model'
import { nextRoutes } from '@/router/next.routes'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Landing',
    component: () => import('@/views/landing/index.vue'),
    meta: { title: ROUTE_TITLES.LANDING, requiresAuth: false }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/index.vue'),
    meta: { title: ROUTE_TITLES.LOGIN, requiresAuth: false }
  },
  {
    path: '/s/:code',
    name: 'SchoolLogin',
    component: () => import('@/views/login/index.vue'),
    meta: { title: ROUTE_TITLES.LOGIN, requiresAuth: false }
  },
  {
    path: '/ops/login',
    name: 'OpsLogin',
    component: () => import('@/views/ops/OpsLoginView.vue'),
    meta: { title: OPS_ROUTE_TITLES.LOGIN, requiresAuth: false }
  },
  {
    path: '/tenant/paused',
    name: 'TenantPaused',
    component: () => import('@/views/error/TenantPausedView.vue'),
    meta: { title: '账号不可用', requiresAuth: false }
  },
  {
    path: '/ops',
    component: () => import('@/layouts/OpsLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: { name: 'OpsTenants' }, meta: { requiresAuth: true } },
      {
        path: 'tenants',
        name: 'OpsTenants',
        component: () => import('@/views/ops/OpsTenantsView.vue'),
        meta: { title: OPS_ROUTE_TITLES.TENANTS, requiresAuth: true }
      },
      {
        path: 'ai-skills',
        name: 'OpsAiSkills',
        component: () => import('@/views/ops/OpsAiSkillsView.vue'),
        meta: { title: OPS_ROUTE_TITLES.AI_SKILLS, requiresAuth: true }
      },
      {
        path: 'ai-models',
        name: 'OpsAiModels',
        component: () => import('@/views/ops/OpsAiModelsView.vue'),
        meta: { title: OPS_ROUTE_TITLES.AI_MODELS, requiresAuth: true }
      },
      {
        path: 'model-context',
        name: 'OpsModelContextCatalog',
        component: () => import('@/views/ops/OpsModelContextCatalogView.vue'),
        meta: { title: OPS_ROUTE_TITLES.MODEL_CONTEXT_CATALOG, requiresAuth: true }
      }
    ]
  },
  // 旧版业务界面（/app）已全面下线：旧链接统一回新版工作台，不再逐页映射。
  {
    path: '/app/:pathMatch(.*)*',
    redirect: '/next/workbench'
  },
  ...nextRoutes,
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/error/404.vue'),
    meta: { title: ERROR_PAGE_TEXT.NOT_FOUND_TITLE }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

router.beforeEach(async (to) => {
  document.title = `${String(to.meta.title || APP_NAME)} - ${APP_FULL_NAME}`

  const userStore = useUserStore(pinia)
  if (!userStore.hasHydrated) {
    await userStore.hydrateSession()
  }

  const requiresAuth = to.meta.requiresAuth === true

  if (requiresAuth && !userStore.isLoggedIn) {
    if (to.path.startsWith('/ops')) {
      return { name: 'OpsLogin', query: { redirect: to.fullPath } }
    }
    return { name: 'Login', query: { redirect: to.fullPath } }
  }

  if (requiresAuth && typeof to.meta.permission === 'string') {
    const permissions = resolveUserPermissions(userStore.userInfo)
    if (permissions && !permissions.has(to.meta.permission)) {
      return { name: 'NextWorkbench', query: { reason: 'forbidden' }, replace: true }
    }
  }

  // 模块订阅守卫：导航已按「已订阅模块」隐藏页面，但直接输入 URL 仍能到达，
  // 这里用同一口径再拦一次。purchasedModules 为 null（平台侧账号）时不限制。
  if (requiresAuth) {
    const moduleCode = moduleOfPath(to.path)
    const purchased = userStore.userInfo?.purchasedModules
    if (moduleCode && Array.isArray(purchased) && !purchased.includes(moduleCode)) {
      return { name: 'NextWorkbench', query: { reason: 'module' }, replace: true }
    }
  }

  if (to.path.startsWith('/ops') && to.name !== 'OpsLogin') {
    if (!isPlatformOperator(userStore.userInfo)) {
      return { name: 'OpsLogin', query: { reason: 'forbidden' } }
    }
  }

  if (to.name === 'OpsLogin' && userStore.isLoggedIn && isPlatformOperator(userStore.userInfo)) {
    return { path: '/ops/tenants', replace: true }
  }

  if ((to.name === 'Login' || to.name === 'SchoolLogin') && userStore.isLoggedIn) {
    return { path: DEFAULT_REDIRECT_PATH }
  }
})

export default router
